import React, { ReactNode } from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sentry } from "@/lib/sentry";

// Wir definieren die Typen für Props und State
interface Props {
  children: ReactNode;
}

function reportClientError(error: Error, errorInfo?: React.ErrorInfo): void {
  try {
    const apiBase = (window as Window & { __SCC_API_BASE__?: string }).__SCC_API_BASE__
      ?? import.meta.env.VITE_API_BASE
      ?? "";
    const payload = {
      type: "js_error",
      errorMessage: error.message,
      stack: (errorInfo?.componentStack
        ? `Component stack:\n${errorInfo.componentStack}\n\nError stack:\n${error.stack ?? ""}`
        : error.stack ?? ""),
      route: window.location.pathname,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };
    // fire-and-forget; never throw
    fetch(`${apiBase}/api/client-error`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // never interfere with the error boundary itself
  }
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Aktualisiert den State, damit beim nächsten Render die Fallback-UI gezeigt wird
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("🔴 Uncaught error:", error, errorInfo);

    // Primary: send to Sentry (no-op when DSN not configured)
    Sentry.captureException(error, {
      contexts: {
        react: { componentStack: errorInfo.componentStack ?? "" },
      },
    });

    // Fallback: also POST to our own backend (works without Sentry DSN)
    reportClientError(error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6 font-sans">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-slate-200">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6 border border-red-100">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>

            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Upps, ein Fehler ist aufgetreten.
            </h1>

            <p className="text-slate-500 mb-6 text-sm leading-relaxed">
              Entschuldige bitte. Deine App ist auf ein unerwartetes Problem gestoßen. Wir haben den
              Fehler protokolliert.
            </p>

            {this.state.error && (
              <div className="bg-slate-100 p-3 rounded-lg text-left text-[10px] font-mono text-slate-500 mb-6 overflow-auto max-h-32 border border-slate-200">
                {this.state.error.toString()}
              </div>
            )}

            <div className="space-y-3">
              <Button
                onClick={this.handleReload}
                className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl shadow-lg shadow-indigo-500/20"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Seite neu laden
              </Button>

              <Button
                onClick={this.handleHome}
                variant="outline"
                className="w-full h-12 border-slate-200 text-slate-600 hover:bg-slate-50 font-medium rounded-xl"
              >
                <Home className="w-4 h-4 mr-2" />
                Zur Startseite
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
